import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userAPI, deviceAPI, documentAPI } from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  ArrowLeftIcon,
  ComputerDesktopIcon,
  ArrowsRightLeftIcon,
  ClockIcon,
  UserIcon,
  MapPinIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [devices, setDevices] = useState([]);
  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('devices');

  // Transfer modal state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [transferring, setTransferring] = useState(false);
  const [transferData, setTransferData] = useState({
    newUserId: '',
    reason: '',
    description: '',
    transferDate: new Date().toISOString().split('T')[0],
    documentFile: null
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [userRes, devicesRes, historyRes, usersRes] = await Promise.all([
        userAPI.getById(id),
        userAPI.getDevices(id),
        userAPI.getHistory(id),
        userAPI.getAll({ isActive: true })
      ]);
      setUser(userRes.data);
      setDevices(devicesRes.data);
      setHistory(historyRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Kullanıcı bilgileri yüklenemedi');
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTransferModal = (device) => {
    setSelectedDevice(device);
    setTransferData({
      newUserId: '',
      reason: '',
      description: '',
      transferDate: new Date().toISOString().split('T')[0],
      documentFile: null
    });
    setShowTransferModal(true);
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setTransferring(true);
    try {
      await deviceAPI.assign(selectedDevice.id, {
        userId: transferData.newUserId,
        assignmentDate: transferData.transferDate,
        description: `Devir sebebi: ${transferData.reason}${transferData.description ? ' - ' + transferData.description : ''}`
      });

      if (transferData.documentFile) {
        const formData = new FormData();
        formData.append('document', transferData.documentFile);
        formData.append('documentType', 'assignment');
        formData.append('description', `Zimmet devir belgesi - ${transferData.reason}`);
        await documentAPI.upload(selectedDevice.id, formData);
      }

      toast.success('Zimmet devri tamamlandı');
      setShowTransferModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Zimmet devri başarısız');
    } finally {
      setTransferring(false);
    }
  };

  const getRoleBadge = (role) => {
    const config = {
      admin: { bg: 'bg-red-100', text: 'text-red-800', label: 'Admin' },
      it: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'IT' },
      user: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Kullanıcı' }
    };
    const c = config[role] || config.user;
    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${c.bg} ${c.text}`}>
        {c.label}
      </span>
    );
  };

  const transferReasons = [
    'İşten ayrılma',
    'Departman değişikliği',
    'Pozisyon değişikliği',
    'Cihaz yükseltme',
    'Cihaz değişimi',
    'Geçici görevlendirme',
    'Diğer'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/users')}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{user.fullName}</h1>
          <p className="text-gray-500">@{user.username}</p>
        </div>
        {getRoleBadge(user.role)}
        <span className={`px-3 py-1 text-sm rounded-full ${user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {user.isActive ? 'Aktif' : 'Pasif'}
        </span>
      </div>

      {/* User Info Card */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <EnvelopeIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <PhoneIcon className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Telefon</p>
              <p className="font-medium">{user.phone || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <BuildingOfficeIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Departman</p>
              <p className="font-medium">{user.departmentInfo?.name || user.department || '-'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <MapPinIcon className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Lokasyon</p>
              <p className="font-medium">{user.location || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-4">
          <button
            onClick={() => setActiveTab('devices')}
            className={`pb-3 px-1 border-b-2 font-medium ${
              activeTab === 'devices'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <ComputerDesktopIcon className="h-5 w-5 inline mr-2" />
            Zimmetli Cihazlar ({devices.length})
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`pb-3 px-1 border-b-2 font-medium ${
              activeTab === 'history'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <ClockIcon className="h-5 w-5 inline mr-2" />
            Zimmet Geçmişi ({history.length})
          </button>
        </nav>
      </div>

      {/* Devices Tab */}
      {activeTab === 'devices' && (
        <div className="bg-white rounded-lg shadow">
          {devices.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ComputerDesktopIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Bu kullanıcının zimmetli cihazı yok</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {devices.map((device) => (
                <div key={device.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      <ComputerDesktopIcon className="h-6 w-6 text-gray-600" />
                    </div>
                    <div>
                      <p
                        className="font-medium text-blue-600 hover:underline cursor-pointer"
                        onClick={() => navigate(`/devices/${device.id}`)}
                      >
                        {device.brand} {device.model}
                      </p>
                      <p className="text-sm text-gray-500">
                        {device.deviceType?.name} • {device.serialNumber}
                      </p>
                      {device.assignmentDate && (
                        <p className="text-xs text-gray-400 mt-1">
                          Zimmet: {format(new Date(device.assignmentDate), 'd MMMM yyyy', { locale: tr })}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => handleOpenTransferModal(device)}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    <ArrowsRightLeftIcon className="h-5 w-5" />
                    Devret
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-lg shadow">
          {history.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ClockIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Zimmet geçmişi bulunamadı</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {history.map((item) => (
                <div key={item.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${item.action === 'assign' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {item.action === 'assign' ? (
                          <UserIcon className="h-5 w-5 text-green-600" />
                        ) : (
                          <ArrowsRightLeftIcon className="h-5 w-5 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">
                          {item.device?.brand} {item.device?.model}
                          <span className="text-gray-400 text-sm ml-2">
                            ({item.device?.deviceType?.name})
                          </span>
                        </p>
                        <p className="text-sm text-gray-500">
                          {item.action === 'assign' ? 'Zimmetlendi' : 'Zimmet kaldırıldı'}
                          {item.description && ` - ${item.description}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {format(new Date(item.assignmentDate), 'd MMM yyyy', { locale: tr })}
                      </p>
                      {item.returnDate && (
                        <p className="text-xs text-gray-500">
                          İade: {format(new Date(item.returnDate), 'd MMM yyyy', { locale: tr })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Transfer Modal */}
      {showTransferModal && selectedDevice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Zimmet Devret</h3>

            {/* Cihaz Bilgisi */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-500 mb-1">Devredilecek Cihaz</p>
              <p className="font-medium">{selectedDevice.brand} {selectedDevice.model}</p>
              <p className="text-sm text-gray-500">{selectedDevice.deviceType?.name} • {selectedDevice.serialNumber}</p>
            </div>

            {/* Mevcut Kullanıcı */}
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-600 mb-1">Mevcut Zimmetli</p>
              <p className="font-medium">{user.fullName}</p>
              <p className="text-sm text-gray-500">{user.departmentInfo?.name || '-'}</p>
            </div>

            <form onSubmit={handleTransfer}>
              <div className="space-y-4">
                {/* Yeni Kullanıcı */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Devredilecek Kişi *
                  </label>
                  <select
                    required
                    value={transferData.newUserId}
                    onChange={(e) => setTransferData({ ...transferData, newUserId: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="">Kişi seçin</option>
                    {users
                      .filter(u => u.id !== parseInt(id))
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.fullName} - {u.departmentInfo?.name || 'Departmansız'}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Sebep */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Devir Sebebi *
                  </label>
                  <select
                    required
                    value={transferData.reason}
                    onChange={(e) => setTransferData({ ...transferData, reason: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="">Sebep seçin</option>
                    {transferReasons.map((reason) => (
                      <option key={reason} value={reason}>{reason}</option>
                    ))}
                  </select>
                </div>

                {/* Tarih */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Devir Tarihi
                  </label>
                  <input
                    type="date"
                    value={transferData.transferDate}
                    onChange={(e) => setTransferData({ ...transferData, transferDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>

                {/* Açıklama */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ek Açıklama
                  </label>
                  <textarea
                    value={transferData.description}
                    onChange={(e) => setTransferData({ ...transferData, description: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    rows={2}
                    placeholder="İsteğe bağlı açıklama..."
                  />
                </div>

                {/* Belge */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Zimmet Belgesi
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => setTransferData({ ...transferData, documentFile: e.target.files[0] })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">PDF, Word veya resim dosyası</p>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowTransferModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={transferring}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={transferring}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {transferring ? 'Devrediliyor...' : 'Devret'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetail;
